import { required, useTranslate } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { NumberInput } from "@/components/admin/number-input";
import { DateInput } from "@/components/admin/date-input";
import { SelectInput } from "@/components/admin/select-input";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import { contactOptionText } from "../misc/ContactOption";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import {
  formatAmount,
  getUserLocale,
  toReportingAmount,
} from "../misc/currency";

export const DealInputs = () => {
  const isMobile = useIsMobile();
  return (
    <div className="flex flex-col gap-8">
      <DealInfoInputs />

      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <DealLinkedToInputs />
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <DealMiscInputs />
      </div>
    </div>
  );
};

const DealInfoInputs = () => {
  return (
    <div className="flex flex-col gap-4 flex-1">
      <TextInput source="name" validate={required()} helperText={false} />
      <TextInput source="description" multiline rows={3} helperText={false} />
    </div>
  );
};

const DealLinkedToInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h3 className="text-base font-medium">
        {translate("resources.deals.inputs.linked_to")}
      </h3>
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteCompanyInput
          label="resources.deals.fields.company_id"
          validate={required()}
          modal
        />
      </ReferenceInput>

      <ReferenceArrayInput source="contact_ids" reference="contacts_summary">
        <AutocompleteArrayInput
          label="resources.deals.fields.contact_ids"
          optionText={contactOptionText}
          helperText={false}
        />
      </ReferenceArrayInput>
    </div>
  );
};

const DealMiscInputs = () => {
  const { dealStages, dealCategories } = useConfigurationContext();
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h3 className="text-base font-medium">
        {translate("resources.deals.field_categories.misc")}
      </h3>

      <SelectInput
        source="category"
        choices={dealCategories}
        optionText="label"
        optionValue="value"
        helperText={false}
      />
      <DealAmountInputs />
      <DateInput
        validate={required()}
        source="expected_closing_date"
        helperText={false}
        defaultValue={new Date().toISOString().split("T")[0]}
      />
      <SelectInput
        source="stage"
        choices={dealStages}
        optionText="label"
        optionValue="value"
        defaultValue="opportunity"
        helperText={false}
        validate={required()}
      />
      <DateInput
        source="created_at"
        label={translate("resources.deals.fields.created_at", {
          _: "Opportunity date",
        })}
        helperText={false}
        parse={(val: string) => {
          if (!val) return val;
          const [y, m, d] = val.split("-").map(Number);
          return new Date(y, m - 1, d).toISOString();
        }}
      />
    </div>
  );
};

/**
 * Amount in the deal's own currency, plus the exchange rate to the reporting
 * currency when they differ (ADR-010). The rate is seeded from settings and
 * editable; the helper line previews what the dashboards will report.
 */
const DealAmountInputs = () => {
  const translate = useTranslate();
  const {
    currency: reportingCurrency,
    dealCurrencies,
    defaultExchangeRates,
  } = useConfigurationContext();
  const { setValue } = useFormContext();
  const currency: string = useWatch({ name: "currency" }) ?? reportingCurrency;
  const amount: number = useWatch({ name: "amount" }) ?? 0;
  const exchangeRate: number = useWatch({ name: "exchange_rate" }) ?? 1;
  const isForeign = currency !== reportingCurrency;

  const currencyChoices = dealCurrencies.map((code) => ({
    id: code,
    name: code,
  }));

  // ra-core forwards the selected value; typed loosely to satisfy the input's union signature
  const handleCurrencyChange = (next: unknown) => {
    const code = String(next);
    const rate =
      code === reportingCurrency ? 1 : (defaultExchangeRates[code] ?? 1);
    setValue("exchange_rate", rate, { shouldDirty: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-start">
        <SelectInput
          source="currency"
          choices={currencyChoices}
          defaultValue={reportingCurrency}
          helperText={false}
          validate={required()}
          onChange={handleCurrencyChange}
          className="w-28 shrink-0"
        />
        <NumberInput
          source="amount"
          defaultValue={0}
          helperText={false}
          validate={required()}
          className="flex-1"
        />
      </div>
      {isForeign && (
        <NumberInput
          source="exchange_rate"
          defaultValue={defaultExchangeRates[currency] ?? 1}
          step={0.0001}
          validate={required()}
          label={translate("resources.deals.fields.exchange_rate", {
            _: "Exchange rate",
          })}
          helperText={translate("resources.deals.inputs.exchange_rate_help", {
            reporting: formatAmount(
              toReportingAmount(amount, exchangeRate),
              reportingCurrency,
              reportingCurrency,
              getUserLocale(),
            ),
            currency,
            reporting_currency: reportingCurrency,
            _: `${reportingCurrency} per 1 ${currency}. Reports as %{reporting}.`,
          })}
        />
      )}
    </div>
  );
};
